import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { CatDrawer } from "blocks-nextjs/blocks-react/components/Drawer";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import React from "react";
import { formatLastReportedTime as formatProjectLastReportedTime, getAssetImageIcon } from "../../pages/Projects/ProjectDetails.helper";
import { useUpdateProjectAssetDetails } from "../../services/query/updates-project-asset-details.query";
import { GHOST_CHARGE_ACTIVITIES } from "../../shared/ghostData";
import useAppConfigStore from "../../store/useAppConfigStore";
import useGlobalStore from "../../store/useGlobalStore";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import { Variant } from "../GlobalNotification/GlobalNotification";
import UnknownMachineHeaderDetails from "../UnknownMashine/UnknownMachineHeaderDetails";
import UnknownMachineHistoricalInfo from "../UnknownMashine/UnknownMachineHistoricalInfo";
import UnknownMachineLiveView from "../UnknownMashine/UnknownMachineLiveView";
import ProductivityStatCards from "../history/ProductivityStatCards";
const UnknownMachineDrawer = ({ assetDrawerActive, setAssetDrawerActive }) => {
    const queryClient = useQueryClient();
    const { appConfig } = useAppConfigStore((state) => ({
        appConfig: state.appConfig
    }));
    const { primaryPartyType, setGlobalNotification, userPartyNumber } = useGlobalStore((state) => ({
        primaryPartyType: state.primaryPartyType,
        setGlobalNotification: state.setGlobalNotification,
        userPartyNumber: state.userPartyNumber
    }));
    const { assetDetails, projectMetaData, projectName } = useProjectDetailsStore((state) => ({
        assetDetails: state.assetDetails,
        projectMetaData: state.projectMetaData,
        projectName: state.projectDetails?.projectName
    }));
    const queryParamsUpdateProjectAssetInfo = {
        ...(userPartyNumber && primaryPartyType !== "CAT" && { partyNumber: userPartyNumber }),
        ...(userPartyNumber && primaryPartyType === "CAT" && { downSelectPartyNumber: userPartyNumber }),
        operationType: "append"
    };
    const projectId = projectMetaData?.projectId || "";
    const { mutate } = useUpdateProjectAssetDetails(projectId, queryParamsUpdateProjectAssetInfo);
    const handleUnknownAssetDetailsSave = (updateAssetDetails) => {
        if (!assetDetails?.assetId || !projectId)
            return;
        mutate({
            assetDetails: [
                {
                    assetId: updateAssetDetails.assetId,
                    assetName: updateAssetDetails.assetName,
                    assetSerialNumber: updateAssetDetails.assetSerialNumber,
                    assetSourceType: updateAssetDetails.assetCategory
                }
            ]
        }, {
            onError: () => {
                setGlobalNotification({
                    message: "Error. Update failed.Please try later",
                    showNotification: true,
                    type: Variant.error
                });
            },
            onSuccess: () => {
                setGlobalNotification({
                    message: "Success. Asset details updated successfully",
                    showNotification: true,
                    type: Variant.success
                });
                queryClient.invalidateQueries({ queryKey: ["projectAssetsDetails", projectId] });
                queryClient.invalidateQueries({ queryKey: ["projectNetwork", projectId] });
            }
        });
    };
    const timezone = projectMetaData?.timeZone || projectMetaData?.timezone || projectMetaData?.offset;
    const isStale = !!assetDetails?.timestamp && new Date(assetDetails.timestamp).getTime() < Date.now() - 24 * 60 * 60 * 1000;
    const reportingStatus = timezone && isStale ? "Not Reporting" : "Reporting";
    const formattedLastReportedTime = formatProjectLastReportedTime(assetDetails?.location?.lastReportedTime, timezone, appConfig?.release_1_1?.showCestTimezone);
    let dischargeEnergySum = 0;
    let diffInMs = 0;
    const chargeActivitiesForAsset = GHOST_CHARGE_ACTIVITIES.filter((activity) => activity.assetId === assetDetails?.assetId);
    chargeActivitiesForAsset.forEach((activity) => {
        const { activity: activityType = "", activityEndTimeStamp = "", activityStartTimeStamp = "", dischargeEnergy = 0 } = activity;
        if (activityType !== "Charging" &&
            activityType !== "AC_Charging" &&
            activityType !== "DC_Charging" &&
            activityType !== "Idle") {
            diffInMs += Math.abs(new Date(activityEndTimeStamp).getTime() - new Date(activityStartTimeStamp).getTime());
        }
        dischargeEnergySum += dischargeEnergy;
    });
    const hours = Math.floor((diffInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffInMs % (1000 * 60 * 60)) / (1000 * 60));
    const dischargeHoursSum = `${hours || "0"}.${minutes || "0"}`;
    return (_jsx(CatDrawer, { "data-test": "unknown-machine-drawer", disableBackdropClick: false, disableEscClick: true, hasBackdrop: true, isActive: assetDrawerActive, isInline: true, onBlDrawerClose: () => {
            setAssetDrawerActive(false);
        }, onBlDrawerOpen: () => { }, preserveContent: false, width: "63%", children: _jsxs(_Fragment, { children: [_jsx("div", { "data-test": "unknown-machine-drawer-header", slot: "header", children: _jsx(CatHeading, { "data-test": "unknown-machine-drawer-title", level: "h1", variant: "title", children: "Asset Details" }) }), _jsxs("div", { className: "-m-4 pb-4", children: [_jsx(UnknownMachineHeaderDetails, { activity: assetDetails?.activity || "Unknown", assetId: assetDetails?.assetId, assetName: assetDetails?.assetName, capacity: assetDetails?.capacity ?? -1, iconUrl: getAssetImageIcon(assetDetails), onApplyChanges: handleUnknownAssetDetailsSave, projectName: projectName || "", reportingStatus: reportingStatus, serialNumber: assetDetails?.assetSerialNumber || "", streetAddress: assetDetails?.location?.streetAddress || "" }), _jsx(UnknownMachineLiveView, { activity: assetDetails?.activity, assetLatestPower: assetDetails?.assetLatestPower, assetStatus: assetDetails?.assetStatus, batteryLevel: assetDetails?.soc !== undefined && assetDetails?.soc !== null
                                ? Number(assetDetails?.soc)
                                : undefined, formattedLastReportedTime: formattedLastReportedTime, hideBatteryLevel: assetDetails?.assetSourceType === "GHOST" && isStale, timeToReady: assetDetails?.timeToReady, timeToStopCharge: assetDetails?.timeToStopCharge }), _jsx(UnknownMachineHistoricalInfo, { assetDrawerActive: assetDrawerActive, assetId: assetDetails?.assetId, projectId: projectMetaData?.projectId, projectTimezone: projectMetaData?.timezone, statCards: _jsx("div", { className: "flex items-center justify-center min-h-5 space-x-4", "data-test": "productivity-stat-cards-historical", children: _jsx(ProductivityStatCards, { dischargeEnergySum: dischargeEnergySum, dischargeHoursSum: dischargeHoursSum, isLoading: false, showDischargeTooltip: false }) }), timezoneOffset: projectMetaData?.offset || "UTC+00:00" })] })] }) }));
};
export default UnknownMachineDrawer;
